import { CHAPERON_API_URL } from "../const/paths"
import { consoleLog } from "../utilities/helpers"

const getAuthHeaders = () => {
    const token = localStorage.getItem("chaperon_auth_token")
    if (!token) {
        const error = new Error("Authentication required. Please log in.")
        error.status = 401
        throw error
    }
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    }
}

/**
 * Send an invitation to join a community.
 * Requires either recipient_email or recipient_user_id (or both).
 *
 * @param {Object} data
 * @param {number} data.community_id
 * @param {string} [data.recipient_email]
 * @param {number} [data.recipient_user_id]
 * @param {string} [data.role] - 'citizen' (default) or 'chief'
 * @param {string} [data.message] - Optional message to include in the invitation email
 * @returns {Promise<Object>}
 */
export async function sendInvitation(data) {
    consoleLog("sendInvitation:", data)

    const response = await fetch(`${CHAPERON_API_URL}/v1/invitation/send`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    })

    const result = await response.json()

    if (!response.ok) {
        const error = new Error(result.message || "Failed to send invitation")
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}

/**
 * Get invitation details by token. Public endpoint - no auth required.
 *
 * @param {string} token - The invitation token
 * @returns {Promise<Object>}
 */
export async function getInvitation(token) {
    consoleLog("getInvitation:", token)

    const response = await fetch(
        `${CHAPERON_API_URL}/v1/invitation/get?invitation_token=${encodeURIComponent(token)}`,
        {
            method: "GET",
            headers: { "Content-Type": "application/json" },
        },
    )

    const result = await response.json()

    if (!response.ok) {
        const error = new Error(result.message || "Failed to get invitation")
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}

/**
 * Accept an invitation and join the community.
 *
 * @param {Object} data
 * @param {string} data.invitation_token
 * @param {number} data.user_id
 * @returns {Promise<Object>}
 */
export async function acceptInvitation(data) {
    consoleLog("acceptInvitation:", data)

    const response = await fetch(`${CHAPERON_API_URL}/v1/invitation/accept`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    })

    const result = await response.json()

    if (!response.ok) {
        const error = new Error(result.message || "Failed to accept invitation")
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}

/**
 * Reject an invitation to join a community.
 *
 * @param {Object} data
 * @param {string} data.invitation_token
 * @param {number} data.user_id
 * @returns {Promise<Object>}
 */
export async function rejectInvitation(data) {
    consoleLog("rejectInvitation:", data)

    const response = await fetch(`${CHAPERON_API_URL}/v1/invitation/reject`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    })

    const result = await response.json()

    if (!response.ok) {
        const error = new Error(result.message || "Failed to reject invitation")
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}

/**
 * List invitations for a user.
 *
 * @param {Object} params
 * @param {number} params.user_id
 * @param {string} [params.type] - 'sent' or 'received'
 * @param {string} [params.status] - 'pending', 'accepted', 'rejected'
 * @returns {Promise<Object>}
 */
export async function listInvitations({ user_id, type, status } = {}) {
    consoleLog("listInvitations:", { user_id, type, status })

    if (user_id === undefined || user_id === null) {
        const error = new Error("User ID is required to list invitations")
        error.status = 400
        throw error
    }

    const params = new URLSearchParams()
    params.set("user_id", String(user_id))
    if (type) params.set("type", type)
    if (status) params.set("status", status)

    const response = await fetch(
        `${CHAPERON_API_URL}/v1/invitation/list?${params.toString()}`,
        {
            method: "GET",
            headers: getAuthHeaders(),
        },
    )

    const result = await response.json()

    if (!response.ok) {
        const error = new Error(result.message || "Failed to list invitations")
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}

/**
 * Resend an invitation with a new token. Cancels the existing pending invitation.
 *
 * @param {Object} data
 * @param {number} data.invitation_id
 * @param {string} [data.message] - Optional new message
 * @returns {Promise<Object>}
 */
export async function resendInvitation(data) {
    consoleLog("resendInvitation:", data)

    const response = await fetch(`${CHAPERON_API_URL}/v1/invitation/resend`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    })

    const result = await response.json()

    if (!response.ok) {
        const error = new Error(result.message || "Failed to resend invitation")
        error.status = response.status
        error.response = result
        throw error
    }

    return result
}
